/**
 * Unified ErrorBoundary
 * @layer atom
 * @package @plerity/ui-core
 *
 * Includes automatic chunk-load recovery: when a lazy-loaded module fails
 * with a 404 (stale content hash after deploy), the page auto-reloads once
 * to fetch fresh HTML with correct asset references. A sessionStorage flag
 * prevents infinite reload loops.
 */

import { recoverFromChunkError, reportCaughtError } from "@plerity/observability";
import React from "react";

export interface ErrorBoundaryProps {
  children: React.ReactNode;
  /** Custom fallback UI. Receives error and reset function when a render function. */
  fallback?: React.ReactNode | ((props: { error: Error; resetError: () => void }) => React.ReactNode);
  /** Human-readable label for what this boundary protects */
  featureLabel?: string;
  /** Called when an error is caught */
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
  /**
   * Heading level of the default fallback's title (default `h3`, unchanged).
   * Use `h2` when the boundary wraps the sole content of a page below its `h1`,
   * `h4` when nested under an `h3` section.
   */
  headingLevel?: "h2" | "h3" | "h4";
  /**
   * When any entry changes while the boundary shows an error, it resets and
   * re-renders its children (e.g. `resetKeys={[recordId]}` so a crash on one
   * record does not persist after navigating to another).
   */
  resetKeys?: ReadonlyArray<unknown>;
}

function keysChanged(prev: ReadonlyArray<unknown> = [], next: ReadonlyArray<unknown> = []): boolean {
  return prev.length !== next.length || prev.some((item, index) => !Object.is(item, next[index]));
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    // Auto-reload once on stale chunk errors (deploy mismatch). If the one
    // allowed reload was already spent this falls through to the normal error UI.
    if (recoverFromChunkError(error)) return;

    console.error(
      `[ErrorBoundary${this.props.featureLabel ? `: ${this.props.featureLabel}` : ""}]`,
      error,
      errorInfo.componentStack
    );
    // Hand the caught error to the runtime reporter (if the app initialised one).
    // ui-core (tier 2) cannot import @plerity/runtime-reporter (tier 3); the
    // reporter registers itself as the process-wide sink in @plerity/observability.
    reportCaughtError(error, {
      boundary: "ui-core",
      label: this.props.featureLabel,
      componentStack: errorInfo.componentStack,
    });
    this.props.onError?.(error, errorInfo);
  }

  componentDidUpdate(prevProps: ErrorBoundaryProps): void {
    if (this.state.hasError && keysChanged(prevProps.resetKeys, this.props.resetKeys)) {
      this.resetError();
    }
  }

  resetError = (): void => {
    this.setState({ hasError: false, error: null });
  };

  render(): React.ReactNode {
    if (!this.state.hasError) {
      return this.props.children;
    }

    const { fallback, featureLabel } = this.props;
    const { error } = this.state;
    // getDerivedStateFromError always sets error alongside hasError=true, so
    // this can't be null in practice — but render defensively rather than assert.
    if (!error) {
      return this.props.children;
    }

    if (fallback) {
      if (typeof fallback === "function") {
        return fallback({ error, resetError: this.resetError });
      }
      return fallback;
    }

    const label = featureLabel || "This component";
    const Heading = this.props.headingLevel ?? "h3";

    return (
      <div
        className="flex min-h-[200px] flex-col items-center justify-center gap-4 rounded-lg border border-red-200 bg-red-50 p-8 dark:border-red-800 dark:bg-red-900/20"
        role="alert"
      >
        <svg
          className="h-10 w-10 text-danger-strong"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          aria-hidden="true"
        >
          <circle cx="12" cy="12" r="10" />
          <line x1="12" y1="8" x2="12" y2="12" />
          <line x1="12" y1="16" x2="12.01" y2="16" />
        </svg>
        <Heading className="text-lg font-semibold text-danger-strong">{label} failed to load</Heading>
        <p className="max-w-md text-center text-sm text-danger-strong">
          An unexpected error occurred. Please try again or refresh the page.
        </p>
        <button
          type="button"
          onClick={this.resetError}
          className="flex items-center gap-2 rounded-md bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2"
        >
          <svg
            className="h-4 w-4"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            aria-hidden="true"
          >
            <path d="M23 4v6h-6M1 20v-6h6" />
            <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
          </svg>
          Try Again
        </button>
      </div>
    );
  }
}

/**
 * HOC that wraps a component with ErrorBoundary.
 */
export function withErrorBoundary<P extends object>(
  Component: React.ComponentType<P>,
  featureLabel: string,
  options?: {
    fallback?: ErrorBoundaryProps["fallback"];
    onError?: ErrorBoundaryProps["onError"];
    headingLevel?: ErrorBoundaryProps["headingLevel"];
  }
): React.FC<P> {
  const displayName = Component.displayName || Component.name || "Component";

  const Wrapped: React.FC<P> = (props) => (
    <ErrorBoundary
      featureLabel={featureLabel}
      fallback={options?.fallback}
      onError={options?.onError}
      headingLevel={options?.headingLevel}
    >
      <Component {...props} />
    </ErrorBoundary>
  );

  Wrapped.displayName = `withErrorBoundary(${displayName})`;
  return Wrapped;
}
